/**
 * Vue.js ベストプラクティス: v-for と v-if の組み合わせ問題を解決するComposable
 * 
 * 問題: v-for と v-if を同じ要素に使用すると、Vueの警告が発生し、
 * パフォーマンスの問題や予期しない動作を引き起こす可能性があります。
 * 
 * 解決策: フィルタリングロジックをcomputedプロパティに分離し、
 * テンプレートでは v-for のみを使用します。
 * 
 * @param {Ref|ComputedRef} sourceList - フィルタリング対象のリスト
 * @param {Function} filterFn - フィルタリング条件を定義する関数
 * @returns {ComputedRef} フィルタリングされたリスト
 * 
 * @example
 * // 基本的な使用例
 * const items = ref([{id: 1, name: 'A'}, {id: 2, name: 'B'}, {id: 3, name: 'C'}]);
 * const filteredItems = useFilteredList(items, item => item.id !== 2);
 * 
 * @example
 * // 複雑な条件での使用例
 * const users = ref([{id: 1, name: 'John', active: true}, {id: 2, name: 'Jane', active: false}]);
 * const activeUsers = useFilteredList(users, user => user.active && user.name.startsWith('J'));
 * 
 * @example
 * // テンプレートでの使用
 * <template>
 *   <div v-for="item in filteredItems" :key="item.id">
 *     {{ item.name }}
 *   </div>
 * </template>
 */

import { computed } from 'vue';

export function useFilteredList(sourceList, filterFn) {
  return computed(() => {
    if (!sourceList.value) {
      return [];
    }
    
    // 配列でない場合は空配列を返す
    if (!Array.isArray(sourceList.value)) {
      console.warn('useFilteredList: sourceList is not an array', sourceList.value);
      return [];
    }
    
    return sourceList.value.filter(filterFn);
  });
}

/**
 * 特定のIDを除外するフィルタリング用のヘルパー関数
 * 
 * @param {string|number} excludeId - 除外するID
 * @returns {Function} フィルタリング関数
 * 
 * @example
 * const items = ref([{id: 'regular'}, {id: 'lantern'}, {id: 'special'}]);
 * const filteredItems = useFilteredList(items, excludeById('lantern'));
 */
export function excludeById(excludeId) {
  return (item) => {
    return item && item.id && item.id !== excludeId;
  };
}

/**
 * 複数のIDを除外するフィルタリング用のヘルパー関数
 * 
 * @param {Array<string|number>} excludeIds - 除外するIDの配列
 * @returns {Function} フィルタリング関数
 * 
 * @example
 * const items = ref([{id: 'regular'}, {id: 'lantern'}, {id: 'special'}]);
 * const filteredItems = useFilteredList(items, excludeByIds(['lantern', 'special']));
 */
export function excludeByIds(excludeIds) {
  return (item) => {
    return item && item.id && !excludeIds.includes(item.id);
  };
}

/**
 * プロパティの値でフィルタリングするヘルパー関数
 * 
 * @param {string} property - フィルタリング対象のプロパティ名
 * @param {*} value - マッチする値
 * @param {boolean} exact - 完全一致かどうか（デフォルト: true）
 * @returns {Function} フィルタリング関数
 * 
 * @example
 * const users = ref([{name: 'John', role: 'admin'}, {name: 'Jane', role: 'user'}]);
 * const admins = useFilteredList(users, filterByProperty('role', 'admin'));
 */
export function filterByProperty(property, value, exact = true) {
  return (item) => {
    if (!item || !item[property]) {
      return false;
    }
    
    if (exact) {
      return item[property] === value;
    } else {
      return item[property].includes(value);
    }
  };
}

/**
 * 複数の条件を組み合わせるフィルタリング関数
 * 
 * @param {Array<Function>} filters - フィルタリング関数の配列
 * @returns {Function} 組み合わせたフィルタリング関数
 * 
 * @example
 * const items = ref([{id: 1, active: true, name: 'A'}, {id: 2, active: false, name: 'B'}]);
 * const filteredItems = useFilteredList(items, combineFilters([
 *   item => item.active,
 *   item => item.name.startsWith('A')
 * ]));
 */
export function combineFilters(filters) {
  return (item) => {
    return filters.every(filter => filter(item));
  };
}
